import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import UploadDropzoneField from 'components/atoms/UploadDropzoneField';
import { v4 as uuidv4 } from 'uuid';
import { Table } from 'components/molecules/Table';
import { fileToBase64, isImage } from 'helpers/image';
import ApplicationStore from 'state/ApplicationStore';
import {
  StyledParagraph,
  StyledStepTitle,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import { attachmentsTableColumns } from './helpers';

export interface IAttachment extends File {
  id: string;
  base64: string | null;
  file: File;
}

interface IAttachmentsStep {
  setNextStep: (arg: number) => void;
}

const AttachmentsStep = ({ setNextStep }: IAttachmentsStep) => {
  const [attachments, setAttachments] = useState<IAttachment[]>([]);

  const acceptFiles = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
  };

  useEffect(() => {
    const handleSubmit = () => {
      ApplicationStore.setStepData('attachmentsData', attachments);
      setNextStep(6);
    };

    document.addEventListener('submitFormEvent', handleSubmit);

    return () => {
      document.removeEventListener('submitFormEvent', handleSubmit);
    };
  }, []);

  useEffect(() => {
    const storeAttachments = ApplicationStore.getStepData('attachmentsData');
    if (Object.keys(storeAttachments).length > 0) {
      setAttachments(storeAttachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlerDelete = (id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  };

  return (
    <Box>
      <StyledStepTitle variant="h4">Attachments</StyledStepTitle>
      <StyledParagraph variant="body2">
        Please upload any relevant attachments.
      </StyledParagraph>

      <Grid
        item
        container
        rowSpacing={0}
        columnSpacing={2}
        xs={12}
        sx={{ marginBottom: '20px' }}
      >
        <Grid item xs={12}>
          <UploadDropzoneField
            onUpload={(files: File[]) => {
              (async () => {
                const uploadedAttachments = await Promise.all(
                  files.map(async (file) => {
                    const base64 = isImage(file) ? await fileToBase64(file) : null;
                    return { ...file, type: file.type, id: uuidv4(), base64, file };
                  }),
                );
                setAttachments((prev) => [...prev, ...uploadedAttachments]);
              })();
            }}
            acceptFiles={acceptFiles}
            description={
              <p>
                Drag & drop TXT file here <br />
                or click to browse
              </p>
            }
          />
        </Grid>
      </Grid>

      {!!attachments.length && (
        <Table
          loading={false}
          columns={attachmentsTableColumns({
            handleDelete: (id: string) => handlerDelete(id),
          })}
          dataSource={attachments}
        />
      )}
    </Box>
  );
};

export default AttachmentsStep;
